import React from 'react'
import { ChevronDownIcon } from '@mote/assets/icons'
import { dataAttr, isNumber, runIfFn } from '@mote/helpers'
import styled, { css, th } from '@xstyled/styled-components'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import {
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownItemText
} from './dropdown.components'
import {
  CLICK_TRANSITION_DURATION,
  HOVER_TRANSITION_DURATION,
  TRIGGER,
  useDropdown
} from './useDropdown'

export const Dropdown = ({
  className,
  triggerElement,
  triggerElementContent,
  showChevron = true,
  ...rest
}) => {
  const { children, menuProps, triggerProps, isOpen, trigger } =
    useDropdown(rest)
  const isHover = React.useMemo(() => trigger === TRIGGER.hover, [trigger])

  const chevron = React.useMemo(() => {
    return showChevron ? (
      <StyledChevronDownIcon
        width='1.5em'
        height='1.5em'
        isOpen={isOpen}
        data-hover={dataAttr(isHover)}
      />
    ) : null
  }, [isOpen, isHover, showChevron])

  const triggerBtn = React.useMemo(() => {
    const screenReaderText = triggerProps['aria-label'] ? (
      <VisuallyHidden>{triggerProps['aria-label']}</VisuallyHidden>
    ) : null

    const localTriggerElement = runIfFn(triggerElement, { isOpen, isHover })

    return !triggerElement ? (
      <TriggerElementWrapper {...triggerProps}>
        {screenReaderText}
        {triggerElementContent}
        {chevron}
      </TriggerElementWrapper>
    ) : (
      React.cloneElement(
        localTriggerElement,
        {
          ...triggerProps,
          ...localTriggerElement.props,
          'data-hover': dataAttr(isHover)
        },
        <>
          {screenReaderText}
          {localTriggerElement.props.children}
          {chevron}
        </>
      )
    )
  }, [isHover, triggerElement, triggerElementContent, triggerProps, chevron])

  return (
    <Root
      data-open={dataAttr(isOpen)}
      data-hover={dataAttr(isHover)}
      className={className}
    >
      {triggerBtn}
      <StyledDropdown
        {...menuProps}
        data-placement={menuProps.placement}
        data-open={dataAttr(isOpen)}
        data-hover={dataAttr(isHover)}
        isHover={isHover}
      >
        {children}
      </StyledDropdown>
    </Root>
  )
}

Dropdown.Item = DropdownItem
Dropdown.Header = DropdownHeader
Dropdown.ItemText = DropdownItemText
Dropdown.Divider = DropdownDivider

const Root = styled.div`
  display: inline-block;
  position: relative;
  max-width: 100%;
`

const StyledDropdown = styled.div`
  position: ${({ inline }) => (inline ? 'relative' : 'absolute')};
  display: flex;
  width: ${({ width }) => (isNumber(width) ? `${width}px` : width)};
  height: ${({ height }) => (isNumber(height) ? `${height}px` : height)};
  border-radius: 0.5rem;
  box-shadow: lg;
  z-index: 40;
  overflow: hidden;
  margin: 0;
  opacity: 0;
  outline: none;
  visibility: hidden;
  transform: ${({ transitionType }) =>
    transitionType === 'scale' ? 'scale(0.9)' : null};
  transition-property: opacity, visibility, transform, box-shadow;
  transition-duration: ${({ isHover }) =>
    isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION}ms;
  transition-timing-function: ease-in;

  &:focus {
    outline: none;
  }

  ${({ variant }) => css`
    background: ${th.color(variant === 'light' ? 'white' : 'black')};
    border: 1px solid ${th.color(variant === 'light' ? 'grey.l2' : 'black')};
  `};

  ${({ orientation }) => {
    switch (orientation) {
      case 'vertical':
        return css`
          flex-direction: column;
          padding: 8px 0;
          overflow-y: auto;
        `
      case 'horizontal':
        return css`
          flex-direction: row;
          padding: 0 8px;
          overflow-x: auto;
        `
    }
  }};

  ${({ placement, inline }) => {
    if (inline) return ''

    switch (placement) {
      case 'bottom-left':
        return css`
          left: 0px;
          margin-top: 10px;
        `
      case 'bottom-right':
        return css`
          right: 0px;
          margin-top: 10px;
        `
      case 'bottom-center':
        return css`
          left: 50%;
          margin-top: 10px;
          transform: translateX(-50%);
        `
      case 'top-center':
        return css`
          left: 50%;
          bottom: 100%;
          margin-bottom: 10px;
          transform: translateX(-50%);
        `
      case 'top-left':
        return css`
          bottom: 100%;
          left: 0px;
          margin-bottom: 10px;
        `
      case 'top-right':
        return css`
          bottom: 100%;
          right: 0px;
          margin-bottom: 10px;
        `
      case 'right':
        return css`
          top: 0;
          left: 100%;
          margin-left: 10px;
        `
      case 'left':
        return css`
          top: 0;
          right: 100%;
          margin-right: 10px;
        `
    }
  }};

  ${({ placement, transitionType, inline }) => {
    if (inline) return ''

    switch (placement) {
      case 'bottom-left': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top left;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(10px);
          `
        break
      }
      case 'bottom-right': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top right;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(10px);
          `

        break
      }
      case 'bottom-center': {
        if (transitionType === 'scale')
          return css`
            transform-origin: top center;
            transform: translateX(-50%) scale(0.9);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, 10px);
          `
        break
      }
      case 'top-center': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom center;
            transform: translateX(-50%) scale(0.9);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, -10px);
          `
        break
      }
      case 'top-left': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom left;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(-10px);
          `
        break
      }
      case 'top-right': {
        if (transitionType === 'scale')
          return css`
            transform-origin: bottom right;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(-10px);
          `
        break
      }
      case 'right': {
        if (transitionType === 'scale')
          return css`
            transform-origin: left center;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateX(-10px);
          `
        break
      }
      case 'left': {
        if (transitionType === 'scale')
          return css`
            transform-origin: right center;
          `

        if (transitionType === 'slide')
          return css`
            transform: translateX(10px);
          `
        break
      }
    }
  }};

  ${({ inline }) =>
    inline &&
    css`
      height: unset;
      max-height: 0;
      border: none;
      border-radius: 0;
      box-shadow: none;
      transition: all 250ms ease-in-out;

      &:focus {
        box-shadow: outline-primary;
      }
    `};

  ${({ inline, isOpen }) =>
    inline &&
    !isOpen &&
    css`
      margin: 0 !important;
      padding: 0 !important;
    `};

  ${({ isOpen, height }) =>
    isOpen &&
    css`
      max-height: ${isOpen ? (height ? `${height}px` : '1000px') : 0};
      opacity: 1;
      visibility: visible;
    `};

  ${({ isOpen, transitionType, placement }) => {
    if (!isOpen) return

    switch (placement) {
      case 'right':
      case 'left':
      case 'bottom-left':
      case 'bottom-right':
      case 'top-left':
      case 'top-right': {
        if (transitionType === 'scale')
          return css`
            transform: scale(1);
          `

        if (transitionType === 'slide')
          return css`
            transform: translateY(0);
          `
        break
      }
      case 'bottom-center':
      case 'top-center': {
        if (transitionType === 'scale')
          return css`
            transform: translateX(-50%) scale(1);
          `

        if (transitionType === 'slide')
          return css`
            transform: translate(-50%, 0);
          `
        break
      }
    }
  }};
`

const TriggerElementWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  border-radius: 4px;
  width: auto;
  cursor: pointer;
  appearance: none;
  user-select: none;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid transparent;
  background: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition-property: all;
  transition-duration: ${CLICK_TRANSITION_DURATION}ms;

  [data-hover] {
    transition-duration: ${HOVER_TRANSITION_DURATION}ms;
  }

  box-sizing: border-box;

  &:active {
    color: ${th.color('primary.d1')};
  }

  &:focus {
    box-shadow: outline-primary;
  }

  &:hover,
  &:focus {
    color: ${th.color('primary.base')};
    background: ${th.color('grey.l4')};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `};
`

const StyledChevronDownIcon = styled(ChevronDownIcon)`
  transition-property: transform;
  transition-duration: ${({ isHover }) =>
    isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION}ms;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(-180deg);
    `};
`
